
import RadioGroup from '@/components/forms/RadioGroup.vue'
import environment from '@/helpers/Environment'
import { findCurrency } from '@/helpers/Currency'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import Price from '@/components/elements/Price.vue'
import type { AnnotatedSession } from '@/types/Sessions'
import type { LanguageStrings } from '@/language/types'

interface Item {
  session: AnnotatedSession
  badges: Badge[]
  label: string
  // TODO Move to <AvailabilityStatus>.
  availability: {
    label: string
    classes: Dict<boolean>
  }
}

interface Badge {
  symbol?: string
  style: 'increase' | MessageStyle
}

@Component({
  name: 'SelectAvailableSession',
  components: { Price, RadioGroup },
})
export default class extends Vue {
  @Prop({ required: true })
  eventSessions: AnnotatedSession[]

  @Prop()
  value: AnnotatedSession

  // TODO Replace with some deep-reaching CSS in <ReserveQuantityFirst>?
  @Prop()
  hideLabel: boolean

  @Prop()
  showPrices: boolean

  @Prop()
  showSoldOutTimes: boolean

  @Prop()
  capacityThreshold: number | undefined

  t: LanguageStrings['selectAvailableSession']

  get sessions(): Item[] {
    const sessions = this.eventSessions.map((session) => this.process(session))

    if (this.showSoldOutTimes) {
      return sessions
    } else {
      return sessions.filter((item) => !item.session.sold_out)
    }
  }

  get selected(): Item | void {
    // Process the input AnnotatedSession into an `Item` that is compatible with the template.
    return this.value ? this.process(this.value) : undefined
  }

  onSelect(session: AnnotatedSession) {
    if (!session.sold_out) this.$emit('input', session)
  }

  @Watch('value')
  onValueUpdate(value) {
    if (value) {
      this.$nextTick(() => {
        this.$el.scrollIntoView({ behavior: 'smooth' })
      })
    }
  }

  private process(session: AnnotatedSession): Item {
    return {
      session,
      label: session.startTime.format('h:mm A'),
      availability: this.availability(session),
      badges: this.badges(session),
    }
  }

  // TODO Move to <AvailabilityStatus>.
  private availability(session: AnnotatedSession): Item['availability'] {
    const low = session.availableCapacity < (this.capacityThreshold ?? 0)
    return {
      label: low ? `${session.availableCapacity} available` : 'Available',
      classes: { danger: low },
    }
  }

  private badges(session: AnnotatedSession): Badge[] {
    const result: Badge[] = []

    if (session.priceIncrease && session.priceIncrease > 0) {
      result.push({
        symbol: findCurrency(environment.portal.default_currency_code)?.symbol,
        style: 'increase',
      })
    }

    for (const annotation of session.annotations) {
      result.push({
        style: annotation.style,
      })
    }

    return result
  }
}
